import { webSocketURL } from '@/services/environment'
import {
  Message,
  PeerConnectionContextType,
  PeerConnectionProviderProps,
  RootState,
  UserData,
  WebSocketConnectionState,
  WebSocketContextType,
  WebSocketProviderProps,
} from '@/types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import { useSelector } from 'react-redux'

const config = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)
const PeerConnectionContext = createContext<PeerConnectionContextType | null>(
  null
)

const MAX_RECONNECTION_ATTEMPTS = 10
const INITIAL_RECONNECT_DELAY = 1000 // 1 second
const MAX_RECONNECT_DELAY = 30000 // 30 seconds

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
}) => {
  const [connectionState, setConnectionState] =
    useState<WebSocketConnectionState>('connecting')
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const socketRef = useRef<WebSocket | null>(null)
  const messageQueue = useRef<Message[]>([])
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const shouldReconnectRef = useRef(true)
  const reconnectAttemptsRef = useRef(0)

  const flushQueue = useCallback(() => {
    const socket = socketRef.current
    while (
      messageQueue.current.length > 0 &&
      socket?.readyState === WebSocket.OPEN
    ) {
      const message = messageQueue.current.shift()
      if (message) {
        socket.send(JSON.stringify(message))
      }
    }
  }, [])

  const createSocket = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return socketRef.current
    }

    const socket = new WebSocket(webSocketURL + url)
    socketRef.current = socket
    setConnectionState('connecting')

    socket.addEventListener('open', () => {
      console.log('WebSocket connected')
      setConnectionState('connected')
      reconnectAttemptsRef.current = 0
      setReconnectAttempts(0)
      flushQueue()
    })

    socket.addEventListener('close', (event) => {
      console.log('WebSocket closed', event.code, event.reason)
      setConnectionState('disconnected')

      if (
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < MAX_RECONNECTION_ATTEMPTS
      ) {
        const currentAttempts = reconnectAttemptsRef.current
        const delay = Math.min(
          INITIAL_RECONNECT_DELAY * Math.pow(2, currentAttempts),
          MAX_RECONNECT_DELAY
        )
        console.log(
          `Attempting to reconnect in ${delay}ms (attempt ${
            currentAttempts + 1
          }/${MAX_RECONNECTION_ATTEMPTS})`
        )
        setConnectionState('reconnecting')
        reconnectAttemptsRef.current = currentAttempts + 1
        setReconnectAttempts(reconnectAttemptsRef.current)

        reconnectTimeoutRef.current = setTimeout(() => {
          createSocket()
        }, delay)
      } else if (reconnectAttemptsRef.current >= MAX_RECONNECTION_ATTEMPTS) {
        console.error('Max reconnection attempts reached')
        setConnectionState('error')
      }
    })

    socket.addEventListener('error', (error) => {
      console.error('WebSocket error:', error)
      setConnectionState('error')
    })

    return socket
  }, [url, flushQueue])

  const sendWebSocketMessage = useCallback((message: Message) => {
    console.log('Sending: ', message)
    const socket = socketRef.current
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message))
    } else {
      messageQueue.current.push(message)
    }
  }, [])

  const disconnectWebSocket = useCallback(() => {
    shouldReconnectRef.current = false
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    const socket = socketRef.current
    if (socket) {
      socket.close(1000, 'User disconnected')
    }
  }, [])

  useEffect(() => {
    shouldReconnectRef.current = true
    reconnectAttemptsRef.current = 0
    const socket = createSocket()

    return () => {
      shouldReconnectRef.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      socket.close(1000, 'Component unmounted')
    }
  }, [url, createSocket])

  const contextValue = useMemo(
    () => ({
      socket: socketRef.current!,
      sendWebSocketMessage,
      disconnectWebSocket,
      connectionState,
      reconnectAttempts,
    }),
    [
      sendWebSocketMessage,
      disconnectWebSocket,
      connectionState,
      reconnectAttempts,
    ]
  )

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const PeerConnectionProvider: React.FC<PeerConnectionProviderProps> = ({
  children,
}) => {
  const peerConnectionRef = useRef(new RTCPeerConnection(config))
  const remoteCandidatesQueue = useRef<RTCIceCandidate[]>([])
  const userData = useAuthUser<UserData>()
  const socketContext = useWebSocket()
  const currentRoomId = useSelector(
    (state: RootState) => state.rooms.selectedRoomId
  )

  const renegotiate = async (
    peerConnection: RTCPeerConnection,
    type: string,
    state: boolean
  ) => {
    try {
      const offer = await peerConnection.createOffer()
      await peerConnection.setLocalDescription(offer)

      socketContext?.sendWebSocketMessage({
        type: 'webrtc-offer',
        offer,
        room_id: currentRoomId,
        sender_id: userData?.user_id,
        [type === 'video'
          ? 'isVideoEnabled'
          : type === 'audio'
          ? 'isMicEnabled'
          : 'isScreenEnabled']: state,
      })
    } catch (error) {
      console.error('Error during renegotiation:', error)
    }
  }

  const startConnection = useCallback(
    async (currentRoomId: string, userData: UserData) => {
      try {
        const peerConnection = peerConnectionRef.current
        if (!peerConnection) throw new Error('PeerConnection not initialized')

        const handleRemoteOffer = async (offer: RTCSessionDescriptionInit) => {
          try {
            await peerConnection.setRemoteDescription(offer)
            const answer = await peerConnection.createAnswer()
            await peerConnection.setLocalDescription(answer)
            socketContext?.sendWebSocketMessage({
              type: 'webrtc-answer',
              answer,
              room_id: currentRoomId,
              sender_id: userData?.user_id,
            })

            // Add queued ICE candidates
            for (const candidate of remoteCandidatesQueue.current) {
              try {
                await peerConnection.addIceCandidate(candidate)
              } catch (error) {
                console.error('Error adding queued ICE candidate:', error)
              }
            }
            remoteCandidatesQueue.current = []
          } catch (error) {
            console.error('Error handling remote offer:', error)
          }
        }

        const handleIceCandidateMessage = async (
          candidateData: RTCIceCandidateInit
        ) => {
          const candidate = new RTCIceCandidate(candidateData)
          if (!peerConnection.remoteDescription) {
            remoteCandidatesQueue.current.push(candidate)
          } else {
            try {
              await peerConnection.addIceCandidate(candidate)
            } catch (error) {
              console.error('Error adding ICE candidate:', error)
            }
          }
        }

        const handleAnswerMessage = async (
          answer: RTCSessionDescriptionInit
        ) => {
          try {
            // NOTE: This code breaks in chrome due to peer connection ssl checks
            await peerConnection.setRemoteDescription(answer)
          } catch (error) {
            console.error('Error setting modified remote description:', error)
          }
        }

        const handleMessage = (e: MessageEvent) => {
          try {
            const message = JSON.parse(e.data)
            console.log('Recieved: ', message)
            if (message.type === 'webrtc-offer') {
              handleRemoteOffer(message.offer)
            } else if (message.type === 'webrtc-ice-candidate') {
              handleIceCandidateMessage(message.candidate)
            } else if (message.type === 'webrtc-answer') {
              handleAnswerMessage(message.answer)
            }
          } catch (error) {
            console.error('Error processing message:', error)
          }
        }

        peerConnection.onicecandidate = (event) => {
          if (event.candidate) {
            socketContext?.sendWebSocketMessage({
              type: 'webrtc-ice-candidate',
              candidate: event.candidate,
              room_id: currentRoomId,
              sender_id: userData?.user_id,
            })
          }
        }

        // Handle incoming tracks
        peerConnection.ontrack = (event) => {
          console.log('🎥 Track received via WebRTC:', {
            trackId: event.track.id,
            kind: event.track.kind,
            label: event.track.label,
            enabled: event.track.enabled,
            readyState: event.track.readyState,
          })

          if (event.streams && event.streams[0]) {
            const stream = event.streams[0]
            console.log(
              `🎥 Received stream: ${stream.id} with ${
                stream.getTracks().length
              } tracks`
            )

            // Forward to room context for processing
            window.dispatchEvent(
              new CustomEvent('webrtc-track', {
                detail: {
                  track: event.track,
                  stream: stream,
                  participantId: userData?.user_id?.toString() || 'unknown',
                },
              })
            )
          }
        }

        socketContext?.socket.addEventListener('message', handleMessage)
      } catch (error) {
        console.error('Error starting connection:', error)
      }
    },
    [peerConnectionRef, socketContext]
  )

  useEffect(() => {
    const peerConnection = peerConnectionRef.current
    if (!userData || !currentRoomId) return

    startConnection(currentRoomId, userData)

    return () => {
      peerConnection?.close()
    }
  }, [currentRoomId, userData, startConnection])

  return (
    <PeerConnectionContext.Provider
      value={{
        connection: peerConnectionRef.current,
        renegotiate,
      }}
    >
      {children}
    </PeerConnectionContext.Provider>
  )
}

// Hook for accessing WebSocket
export const useWebSocket = () => useContext(WebSocketContext)

// Hook for accessing PeerConnection
export const usePeerConnection = () => useContext(PeerConnectionContext)
